import { useEffect, useState, useCallback, useRef } from 'react';
import referralService from '@/services/referral/referralService';

// permissions
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { Container } from '@/components/container';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
// data tables import
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { SelectInput, DateInput, REFRAL_CONTACT_STATUS_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import { FiltersToFormData, getAccessToken } from '@/utils';
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { goToLogin, goTo403 } from "@/utils/Navigation";
import { createRoot } from 'react-dom/client';
import { data } from 'jquery';
import { useAuthContext } from '@/auth';
import { FormattedMessage, useIntl } from 'react-intl';

// init
DataTable.use(DT);

const ReferralRequestAttorney = () => {
    const { logout, auth } = useAuthContext();
    const referralAttorneyID = auth?.user?.referral_attorney === 1 ? auth?.user?.id : null
    const [filterData, setFilterData] = useState({
        dob: null,
        do_injury: null,
        created_by: referralAttorneyID,
        date_range: null,
        from_date: null,
        to_date: null,
        status: null,
    });
    const tableRef = useRef(null);
    const API_URL = import.meta.env.VITE_APP_BASE_URL;
    const token = getAccessToken();
    const filterRef = useRef(filterData);
    const [showFilter, setShowFilter] = useState(false)
    const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
    const { ability } = useAbility();
    const canCreatedReffral = ability.can('handle', 'referral-contact');
    const { locale, formatMessage } = useIntl();

    useEffect(() => {
        filterRef.current = filterData;
    }, [filterData]);


    // set columns for data table
    const columns = [
        // {
        //     title: 'S#',
        //     data: null,
        //     className: 'table_serial_num',
        //     center: true,
        //  orderable: true,se,
        //     render: function (data, type, row, meta) {
        //         return meta.row + 1;
        //     },
        //     name: 'serial'
        // },
        {
            title: formatMessage({ id: 'REFERRED.CONTACT_NAME' }),
            data: 'p_name',
            name: 'p_name',
        },
        {
            title: formatMessage({ id: 'REFERRED.CONTACT_NUMBER' }),
            data: 'phones',
            name: 'phones',
            orderable: true,
            render: function (data, type, row) {
                if (!Array.isArray(row.phones) || !row.phones.length) return '-';
                return row.phones.map(p => `${p.number}`).join('<br>');
            }
        },
        {
            data: 'speciality',
            title: formatMessage({ id: 'REFERRED.PROVIDER_SPECIALTY' }),
            name: 'speciality'
        },
        {
            data: 'dob',
            title: formatMessage({ id: 'REFERRED.DATE_OF_BIRTH' }),
            name: 'dob'
        },
        {
            data: 'do_injury',
            title: formatMessage({ id: 'REFERRED.DATE_OF_INJURY' }),
            name: 'do_injury'
        },
        {
            data: 'created_at',
            title: formatMessage({ id: 'REFERRED.CREATED_AT' }),
            name: 'created_at'
        },
        {
            data: 'status',
            title: formatMessage({ id: 'REFERRED.STATUS' }),
            orderable: true,
            name: 'status',
            render: function (value) {
                const badgeClass = value === 1 ? 'badge-success' : 'badge-warning';
                const label = value === 1 ? 'Approved' : 'Pending';
                return `<span class="badge badge-pill badge-outline ${badgeClass} gap-1 items-center">
                <span class="badge badge-dot size-1.5 ${badgeClass}"></span>
                ${label}
            </span>`;
            }
        },
        // {
        //     title: formatMessage({ id: 'REFERRED.ACTIONS' }),
        //     data: null,
        //     orderable: true,
        //     searchable: false,
        //     name: 'actions',
        //     render: function (data, type, row) {
        //         return `<div id="action-cell-${row.id}" className="w-[35px]"> </div>`;
        //     }
        // },

        // --- EXTENDED COLUMNS ---
        // { data: 'id', title: 'ID', name: 'id' },
        // { data: 'doctor_name', title: 'Doctor Name', name: 'doctor_name' },
        // { data: 'file_upload', title: 'File Upload', name: 'file_upload' },
        // { data: 'file_upload_name', title: 'File Upload Name', name: 'file_upload_name' },
        { data: 'note', title: formatMessage({ id: 'REFERRED.NOTE' }), name: 'note' },
        {
            data: 'address',
            title: formatMessage({ id: 'REFERRED.ADDRESS' }),
            name: 'address',
            render: (value) => {
                if (!Array.isArray(value) || !value.length) return '-';
                return value.map(a => `${a.add_type ?? ''}: ${a.address ?? ''}, ${a.city ?? ''}, ${a.state ?? ''}, ${a.zip ?? ''}`).join('<br>');
            }
        },
        // { data: 'custom_fields', title: 'Custom Fields', name: 'custom_fields' },
        // { data: 'customFields', title: 'Custom Fields (Camel)', name: 'customFields' },
        // { data: 'deleted_at', title: 'Deleted At', name: 'deleted_at' },
        // { data: 'updated_at', title: 'Updated At', name: 'updated_at' },
        // { data: 'DT_RowIndex', title: 'Row Index', name: 'DT_RowIndex' },
    ];


    const defaultVisibleColumns = [
        // 'serial',
        'p_name',
        'phones',
        'speciality',
        'dob',
        'do_injury',
        'created_at',
        'status',
        // 'actions'
    ];

    const ajaxConfig = createDataTableConfig('/referral-contact-list', filterRef, token, logout);

    const options = createDataTableOptions({
        order: [[5, 'desc']],
        initComplete: function () {
            const api = this.api();
            api.columns().every(function () {
                const col = this;
                const name = col.settings()[0].aoColumns[col.index()].name;
                if (name && !defaultVisibleColumns.includes(name)) {
                    col.visible(false);
                }
            });
        },
        drawCallback: function () {
            const api = this.api();
            const rows = api.rows({ page: 'current' }).data();
            rows.each((row) => {

                const actionContainer = document.getElementById(`action-cell-${row.id}`);
                if (actionContainer) {
                    if (!actionContainer._reactRoot) {
                        actionContainer._reactRoot = createRoot(actionContainer);
                    }
                    actionContainer._reactRoot.render(renderCustomActions(row));
                }
            });
        },
    });

    const resetFilters = () => {
        const cleared = {
            dob: null,
            do_injury: null,
            // created_by: referralAttorneyID,
            date_range: null,
            from_date: null,
            to_date: null,
            status: null,
        };

        setFilterData(cleared);
        filterRef.current = cleared;
        refresh()
    };

    const handleFilterChange = (key, val) => {
        setFilterData((prev) => {
            const updated = { ...prev, [key]: val };
            filterRef.current = updated;
            //console.log('updated',updated)
            return updated;
        });
    };

    const handleToggleFilter = () => {
        setShowFilter(prev => !prev)
    }

    const refresh = () => {
        const table = tableRef.current.dt();
        table.ajax.reload();
    };

    const handleAddReferral = async (id) => {
        try {
            const { status, data } = await referralService.createRefferedAttorneyContact(id);

            if (status) {
                refresh()
                toast.success(formatMessage({ id: 'REFERRED.CONTACT_CREATED_TITLE' }), {
                    description: `${data.first_name + ' ' + data.last_name} ${formatMessage({ id: 'REFERRED.CONTACT_CREATED_SUCCESS' })}`,
                });
            }
        } catch (error) {
            console.error('Error', error)
            toast.error(formatMessage({ id: 'referral.failed_to_add_referral' }), {
                description: formatMessage({ id: 'referral.error_message' }),
            });
        }
    };

    // Custom Render Func For Jquery Data tbale
    const renderCustomActions = (row) => {
        const { id, status } = row;
        return (
            <>
                <DropdownMenu>
                    {status === 0 && canCreatedReffral && (
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-gray-600">
                                <KeenIcon icon="dots-vertical" className="text-[18px]" />
                            </Button>
                        </DropdownMenuTrigger>
                    )}
                    <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => handleAddReferral(id)}>
                            <KeenIcon icon="address-book" className="mr-0.5 h-4 w-4 cursor-pointer" /> <FormattedMessage id="REFERRED.CREATE_CONTACT" />
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </>
        );
    };


    return (
        <>
            <Container>
                <FilterContainer
                    show={showFilter}
                    onApply={() => refresh()}
                    onReset={resetFilters}
                >
                    <DateInput
                        customClass="sm:w-[32%] w-full"
                        label={formatMessage({ id: 'REFERRED.CREATED_AT_RANGE' })}
                        mode="range"
                        value={filterData.date_range}
                        onChange={(range) => {
                            setFilterData(prev => ({
                                ...prev,
                                from_date: range?.from ?? null,
                                to_date: range?.to ?? null,
                                date_range: range,
                            }));
                        }}
                    />

                    <DateInput
                        customClass="sm:w-[32%] w-full"
                        label={formatMessage({ id: 'REFERRED.DATE_OF_BIRTH' })}
                        value={filterData.dob}
                        onChange={(date) =>
                            setFilterData((prev) => ({
                                ...prev,
                                dob: date,
                            }))
                        }
                    />

                    <DateInput
                        customClass="sm:w-[32%] w-full"
                        label={formatMessage({ id: 'REFERRED.DATE_OF_INJURY' })}
                        value={filterData.do_injury}
                        onChange={(date) =>
                            setFilterData((prev) => ({
                                ...prev,
                                do_injury: date,
                            }))
                        }
                    />

                    <SelectInput
                        label={formatMessage({ id: 'REFERRED.STATUS' })}
                        value={filterData.status}
                        onChange={(val) => handleFilterChange('status', val)}
                        staticOptions={REFRAL_CONTACT_STATUS_OPTIONS}
                        customClass="sm:w-[32%] w-full"
                    />
                    {/* <TextInput
                            label="Zip Code"
                            value={filterData.zip}
                            onChange={(val) => handleFilterChange('zip', val)}
                            placeholder="Enter zip"
                            customClass="sm:w-[32%] w-full"
                            type="number"
                        /> */}
                </FilterContainer>

                {/* body */}
                <div className="min-w-full mb-4 card">
                    <div className="card-header !block">
                        <div className='flex flex-wrap items-center justify-between'>
                            <div className='flex items-center gap-1'>
                                <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
                                {/* <h3 className="card-title"><FormattedMessage id="REFERRED.REFERRED_CONTACT" /></h3> */}
                            </div>

                            <div className='flex flex-wrap items-center gap-2'>
                                {/* <Can I="create" a="users" ability={ability}> */}

                                {/* </Can> */}
                                <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                                    <KeenIcon icon="filter" className="w-fit btn-icon" />
                                    {showFilter ? <FormattedMessage id="FILTERS.HIDE" /> : <FormattedMessage id="FILTERS.SHOW" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <DataTable
                        id="reffral-request-table"
                        ajax={ajaxConfig}
                        columns={columns}
                        options={options}
                        ref={tableRef}
                        key={locale}
                    />

                    {/* <ConfirmationModal
                        show={isOpen}
                        onClose={closeModal}
                        onConfirm={reffralReq}
                        message="Are you sure you want to update"
                        title="Are You Sure?"
                    /> */}
                </div>
            </Container>
        </>
    )
}

export { ReferralRequestAttorney }
